
import Component from "vue-class-component"
import PageHeader from "@/superannuation/PageHeader.vue"
import HelpIcon from "@/ui/HelpIcon.vue"
import Vue from "vue"
import {Asset, AssetBalance, Member, Plan, Platform} from "@/superannuation/PlanService"
import SelectDropDown from "@/components/SelectDropDown.vue"
import {restDownloadFile, restPostDownloadFile} from "@/utils/rest"
import Axios, {AxiosError, AxiosResponse} from "axios"
import {formatMoney, formatPercent} from "@/utils/formatter"
import {theme} from "@/theme"
import ErrorMessage from "@/components/ErrorMessage.vue"
import {format} from "date-fns"
import {Inject} from "vue-property-decorator"
import {AuthenticationService} from "@/AuthenticationService"

export interface FeeComparisonRequest extends Record<string, any> {
  memberId: string | undefined
  memberName: string | null
  planId: string | null
  assetId: string | null
  balance: number
  comparisonAssetId: string
}

export interface FeeComparison {
  memberName: string
  planName: string
  balance: number
  assetBalances: Map<string, number>
  assetFeesA: AssetFees
  assetFeesB: AssetFees
  adminFees: Array<Fee>
  investmentFees: Array<Fee>
}

export interface AssetFees {
  platformName: string
  effectiveDate: Date
  assetName: string
  adminFeeDollars: number
  adminFeePercent: number
  trusteeFeePercent: number
  adminFeeTotalDollars: number
  investmentFeePercent: number
  idcFeePercent: number
  performanceFeePercent: number
  investmentFeeTotalPercent: number
  investmentFeeTotalDollars: number
  feeTotalDollars: number
}

export interface Fee {
  name: string
  amountA: number
  amountB: number
  inDollars: boolean
  asterisk: boolean
}

@Component({
  components: {
    ErrorMessage,
    SelectDropDown,
    PageHeader,
    HelpIcon,
  },
})
export default class FeeComparisonView extends Vue {
  @Inject()
  authenticationService: AuthenticationService

  t = theme()

  readonly memberOptionExisting = "EXISTING_MEMBER"
  readonly memberOptionNew = "NEW_MEMBER"
  readonly memberOptions = [
    {label: "Existing", value: this.memberOptionExisting},
    {label: "New Member", value: this.memberOptionNew},
  ]

  $refs: {
    planSelect: SelectDropDown
    memberSelect: SelectDropDown
    platformSelect: SelectDropDown
    assetSelect: SelectDropDown
    fromAssetSelect: SelectDropDown
  }

  planId: string | null = null
  platformId: string | null = null
  assetId: string | null = null
  fromAssetId: string | null = null

  memberOption = this.memberOptionExisting

  member: Member | null = null
  fromPlatform: Platform | null = null

  balance = 0
  newMemberName: string | null = null

  message: string | null = null

  feeComparisonInProgress = false
  feeComparisonData: FeeComparison | null = null

  isAdmin = false

  async created() {
    this.isAdmin = await this.authenticationService.isAdmin()
  }

  get isFeeComparisonDisabled(): boolean {
    return (
      this.assetId == null ||
      (this.isNewMember &&
        (this.newMemberName == null ||
          this.planId == null ||
          this.balance <= 0 ||
          this.fromAssetId == null)) ||
      (!this.isNewMember && this.member == null)
    )
  }

  get selectedPlanPlatform(): string | null {
    return this.fromPlatform == null ? null : this.fromPlatform.name
  }

  get isNewMember(): boolean {
    return this.memberOption === this.memberOptionNew
  }

  get memberAssetBalances(): Array<AssetBalance> | null {
    return this.member == null ? null : this.member.assetBalances
  }

  private getFee(fee: Fee): Record<string, string> {
    return {
      name: fee.name,
      a: fee.inDollars ? formatMoney(fee.amountA) : formatPercent(fee.amountA),
      b: fee.inDollars ? formatMoney(fee.amountB) : formatPercent(fee.amountB),
    }
  }

  get feeComparisonDataRows(): Array<Record<string, unknown>> | null {
    if (this.feeComparisonData == null) {
      return null
    }

    const feeComparisonDataRows: Array<Record<
      string,
      unknown
    >> = this.feeComparisonData.adminFees.map(fee => this.getFee(fee))

    const assetFeesA = this.feeComparisonData.assetFeesA
    const assetFeesB = this.feeComparisonData.assetFeesB

    feeComparisonDataRows.push({
      name: "Total Admin Cost ($)",
      a: formatMoney(assetFeesA.adminFeeTotalDollars),
      b: formatMoney(assetFeesB.adminFeeTotalDollars),
    })

    feeComparisonDataRows.push(
      ...this.feeComparisonData.investmentFees.map(fee => this.getFee(fee))
    )

    feeComparisonDataRows.push(
      {
        name: "Total Investment related Fees ($)",
        a: formatMoney(assetFeesA.investmentFeeTotalDollars),
        b: formatMoney(assetFeesB.investmentFeeTotalDollars),
      },
      {
        name: "Total Cost",
        a: formatMoney(assetFeesA.feeTotalDollars),
        b: formatMoney(assetFeesB.feeTotalDollars),
      }
    )

    return feeComparisonDataRows
  }

  private planSelected(plan: Plan | null) {
    this.planId = plan == null ? null : plan.id
    if (plan == null) {
      this.fromPlatform = null
    } else {
      if (this.isNewMember) {
        this.$refs.fromAssetSelect.model = null
      } else {
        this.$refs.memberSelect.model = null
        this.member = null
      }
      this.fromPlatform = plan.platform
      this.fromAssetId = null
    }
  }

  private memberSelected(member: Member | null) {
    this.member = member
  }

  private platformSelected(platform: Platform | null) {
    this.platformId = platform == null ? null : platform.id
    this.assetId = null
    this.$refs.assetSelect.model = null
  }

  private assetSelected(asset: Asset | null) {
    this.assetId = asset == null ? null : asset.id
  }

  private fromAssetSelected(asset: Asset | null) {
    this.fromAssetId = asset == null ? null : asset.id
  }

  private memberOptionSelected() {
    if (this.isNewMember) {
      this.$refs.memberSelect.model = null
      this.member = null
    } else {
      this.$refs.fromAssetSelect.model = null
    }
  }

  private assetOptionsPath(): string | null {
    return this.platformId == null ? null : `/api/platforms/${this.platformId}/assets`
  }

  private fromAssetOptionsPath(): string | null {
    return this.fromPlatform == null
      ? null
      : `/api/platforms/${this.fromPlatform.id}/assets?excludeChoice=true`
  }

  private memberLabel(): (option: Member) => string {
    return (option: Member) => `${option.surname} [${option.login}] - ${option.companyName}`
  }

  private assetLabel(): (option: Asset) => string {
    return (option: Asset) => `${option.name} - ${option.description}`
  }

  private getFeeComparisonRequest(): FeeComparisonRequest {
    return {
      planId: this.planId,
      memberId: this.member?.id,
      memberName: this.newMemberName,
      assetId: this.fromAssetId,
      balance: this.balance,
      comparisonAssetId: this.assetId!,
    }
  }

  private async downloadFeeComparison() {
    if (!this.isFeeComparisonDisabled) {
      this.feeComparisonInProgress = true
      await restPostDownloadFile(
        `/api/feeComparison`,
        `FeeComparison.pdf`,
        this.getFeeComparisonRequest()
      )
        .then(() => (this.message = null))
        .catch((reason: AxiosError<any>) => {
          this.message = reason.response?.data.message
        })
        .finally(() => (this.feeComparisonInProgress = false))
    }
  }

  private async runFeeComparison() {
    if (!this.isFeeComparisonDisabled) {
      this.feeComparisonInProgress = true
      void (await Axios.post(`/api/feeComparison/data`, this.getFeeComparisonRequest())
        .then((response: AxiosResponse) => {
          this.message = null
          this.feeComparisonData = response.data
        })
        .catch((reason: AxiosError<any>) => {
          this.message = reason.response?.data.message
        })
        .finally(() => (this.feeComparisonInProgress = false)))
    }
  }

  private async downloadComparisonsExport() {
    const now = format(new Date(), "ddMMyyyy")
    await restDownloadFile(`/api/feeComparison/exportXlsx`, `FeeComparisons - ${now}.xlsx`)
      .then(() => (this.message = null))
      .catch((reason: AxiosError<any>) => {
        this.message = reason.response?.data.message
      })
  }
}
